import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ProgressBar from "react-bootstrap/ProgressBar";
import Badge from "react-bootstrap/Badge";
import {
  BookOpen,
  Trophy,
  Target,
  Clock,
  CheckCircle,
  Flame,
  ArrowRight,
} from "lucide-react";
import {
  getMe,
  getLessonProgress,
  getQuizResults,
  listModules,
  getModule,
} from "../api/client";
import { useTranslation } from "../hooks/useTranslation";

export default function StudentDashboard() {
  const { t } = useTranslation(); // <-- HINZUFÜGEN!
  
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [modules, setModules] = useState([]);
  const [fullModules, setFullModules] = useState([]);
  const [openQuizzes, setOpenQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const findLessonProgress = (lessonId) =>
    progress.find((p) => p.lesson_id === lessonId) || {
      progress_percent: 0,
      status: "not_started",
      time_spent_minutes: 0,
      completed_date: null,
    };

  // 1) Basisdaten laden
  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        if (!me || !me.id) {
          setUser(null);
          setLoading(false);
          return;
        }

        const [lp, qr, mods] = await Promise.all([
          getLessonProgress(me.id),
          getQuizResults(me.id),
          listModules(),
        ]);

        setUser(me);
        setProgress(Array.isArray(lp) ? lp : []);
        setQuizResults(Array.isArray(qr) ? qr : []);
        const sorted =
          (Array.isArray(mods) ? mods : []).sort(
            (a, b) => (a.order_index || 0) - (b.order_index || 0)
          ) || [];
        setModules(sorted);
      } catch (err) {
        console.error("Fehler beim Laden des Dashboards (Basisdaten):", err);
        setProgress([]);
        setQuizResults([]);
        setModules([]);
      }
      setLoading(false);
    })();
  }, []);

  // 2) Vollständige Modul-JSONs laden
  useEffect(() => {
    (async () => {
      if (!modules.length) {
        setFullModules([]);
        return;
      }
      try {
        const loaded = await Promise.all(
          modules.map((m) =>
            getModule(m.id).catch((e) => {
              console.error("getModule in StudentDashboard error:", e);
              return null;
            })
          )
        );
        setFullModules(loaded.filter(Boolean));
      } catch (e) {
        console.error("fullModules load error:", e);
        setFullModules([]);
      }
    })();
  }, [modules]);

  // 3) Alle Lessons flach machen
  const allLessons = useMemo(() => {
    const arr = [];
    fullModules.forEach((mod) => {
      (mod.lessons || []).forEach((lesson) => {
        arr.push({
          moduleId: mod.id,
          moduleTitle: mod.title,
          year_level: Number(mod.year_level) || 0,
          semester: Number(mod.semester) || 0,
          moduleOrder: mod.order_index || 0,
          lessonOrder: lesson.order_index || 0,
          lessonId: lesson.id,
          lesson,
        });
      });
    });

    arr.sort((a, b) => {
      if (a.year_level !== b.year_level) return a.year_level - b.year_level;
      if (a.semester !== b.semester) return a.semester - b.semester;
      if (a.moduleOrder !== b.moduleOrder)
        return a.moduleOrder - b.moduleOrder;
      return a.lessonOrder - b.lessonOrder;
    });

    return arr;
  }, [fullModules]);

  // 4) Offene Quiz bestimmen
  useEffect(() => {
    const completedQuizIds = new Set(
      (quizResults || []).map((r) => r.quiz_id)
    );

    const result = [];
    fullModules.forEach((mod) => {
      (mod.lessons || []).forEach((lesson) => {
        if (!Array.isArray(lesson.exercises) || !lesson.exercises.length)
          return;
        const qid = lesson.id;
        if (completedQuizIds.has(qid)) return;

        result.push({
          moduleId: mod.id,
          moduleTitle: mod.title,
          lessonId: lesson.id,
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration_minutes,
        });
      });
    });

    setOpenQuizzes(result);
  }, [fullModules, quizResults]);

  // 5) KPIs
  const totalProgress = progress.length
    ? Math.round(
        progress.reduce((a, p) => a + (p.progress_percent || 0), 0) /
          progress.length
      )
    : 0;

  const totalPoints = useMemo(
    () => quizResults.reduce((acc, r) => acc + (r.score || 0), 0),
    [quizResults]
  );

  const totalQuizzesDone = quizResults.length;

  const dateKey = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const streakDays = useMemo(() => {
    const activeDays = new Set();

    (quizResults || []).forEach((r) => {
      if (!r.created_date) return;
      const d = new Date(r.created_date);
      const key = dateKey(d);
      if (key) activeDays.add(key);
    });

    (progress || []).forEach((p) => {
      if (p.status !== "completed" || !p.completed_date) return;
      const d = new Date(p.completed_date);
      const key = dateKey(d);
      if (key) activeDays.add(key);
    });

    if (!activeDays.size) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let streak = 0;
    const cur = new Date(today);

    while (true) {
      const key = dateKey(cur);
      if (activeDays.has(key)) {
        streak += 1;
        cur.setDate(cur.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  }, [quizResults, progress]);



  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">{t.common.loading}</span>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <p>{t.common.error}</p>
      </Container>
    );
  }

  const lessonsToShow = allLessons.filter((item) => {
    const lp = findLessonProgress(item.lessonId);
    return lp.status !== "completed";
  })
  .slice(0, 3);
    
  return (
    <>
    
      <main className="dashboard-main" style={{ marginLeft: isDesktop ? '240px' : '0', backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
        
      <Container className="py-4" style={{ maxwidth: 'none', paddingLeft: isDesktop ? '2rem' : '1rem', paddingRight: isDesktop ? '2rem' : '1rem'}}>
      {/* Header */}
      
      <div className="mb-4">
        <h1 className="mb-2">
          {t.common.welcomeBack}, {user.full_name || user.name}!
        </h1>
        <p className="text-muted mb-0">{t.dashboard.readyForLesson}</p>
      </div>

      {/* KPI Cards */}
      <Row className="g-3 mb-4">
        <Col lg={4} md={6} sm={12}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div
                className="rounded-circle p-3 me-3"
                style={{ backgroundColor: "#e3f2fd" }}
              >
                <Trophy size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="mb-0 fw-bold">{totalPoints}</h3>
                <p className="text-muted mb-0 small">{t.quizzes.pointsEarned}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={6} sm={12}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div
                className="rounded-circle p-3 me-3"
                style={{ backgroundColor: "#fff3e0" }}
              >
                <Flame size={24} className="text-warning" />
              </div>
              <div>
                <h3 className="mb-0 fw-bold">{streakDays}</h3>
                <p className="text-muted mb-0 small">{t.dashboard.daysStreak}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={6} sm={12}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div
                className="rounded-circle p-3 me-3"
                style={{ backgroundColor: "#e8f5e9" }}
              >
                <CheckCircle size={24} className="text-success" />
              </div>
              <div>
                <h3 className="mb-0 fw-bold">{totalQuizzesDone}</h3>
                <p className="text-muted mb-0 small">{t.quizzes.completed}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Deine Lektionen */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">{t.dashboard.yourLessons}</h2>
          <Link to="/Lessons">
            <Button variant="outline-primary" size="sm">
              {t.dashboard.viewAll} <ArrowRight size={16} className="ms-1" />
            </Button>
          </Link>
        </div>

        <Row className="g-3">
          {lessonsToShow.map((item) => {
            const lesson = item.lesson;
            const lp = findLessonProgress(item.lessonId);
            const isCompleted = lp.status === "completed";

            return (
              <Col md={4} key={item.lessonId}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-start mb-3">
                      <div
                        className="rounded p-2 me-2"
                        style={{ backgroundColor: "#f5f5f5" }}
                      >
                        <BookOpen size={20} className="text-secondary" />
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-1">{lesson.title}</h5>
                        <Badge
                          bg={
                            isCompleted
                              ? "success"
                              : lp.status === "in_progress"
                              ? "primary"
                              : "secondary"
                          }
                          className="small"
                        >
                          {isCompleted
                            ? t.lessons.completed
                            : lp.status === "in_progress"
                            ? t.dashboard.inProgress
                            : t.dashboard.notStarted}
                        </Badge>
                      </div>
                    </div>

                    <ProgressBar
                      now={lp.progress_percent || 0}
                      label={`${lp.progress_percent || 0}%`}
                      className="mb-2"
                      variant={isCompleted ? "success" : "secondary"}
                    />

                    <p className="text-muted small mb-2">
                      {lesson.description}
                    </p>
                    <div className="d-flex align-items-center text-muted small mb-3">
                      <Clock size={14} className="me-1" />
                      {lesson.duration_minutes} {t.quizzes.minutes}
                    </div>
                    <Link
                      to={`/module/${encodeURIComponent(
                        item.moduleId
                      )}/lesson/${encodeURIComponent(item.lessonId)}`}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-100"
                      >
                        {isCompleted
                          ? t.dashboard.repeat
                          : lp.status === "in_progress"
                          ? t.lessons.continue
                          : t.lessons.start}
                      </Button>
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {lessonsToShow.length === 0 && (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <BookOpen size={48} className="text-muted mb-3" />
              <h5 className="mb-2">{t.dashboard.noLessons}</h5>
              <p className="text-muted mb-3">
                {t.dashboard.startFirstLesson}
              </p>
              <Link to="/Lessons">
                <Button variant="primary">{t.dashboard.discoverLessons}</Button>
              </Link>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Offene Quiz */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">{t.dashboard.openQuizzes}</h2>
          <Link to="/Quizzes">
            <Button variant="outline-primary" size="sm">
              {t.dashboard.allQuizzes} <ArrowRight size={16} className="ms-1" />
            </Button>
          </Link>
        </div>

        {openQuizzes.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <Card.Body className="py-4 text-center">
              <Trophy size={40} className="text-success mb-2" />
              <h6 className="mb-1">{t.dashboard.allQuizzesDone}</h6>
              <p className="text-muted mb-0">
                {t.dashboard.noOpenQuizzes}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-3">
            {openQuizzes.slice(0, 3).map((q) => (
              <Col md={4} key={`${q.moduleId}_${q.lessonId}`}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg="info">{q.moduleTitle}</Badge>
                      <Badge bg="secondary">{t.dashboard.open}</Badge>
                    </div>
                    <h5 className="mb-1">{q.title}</h5>
                    <p className="text-muted small mb-3">
                      {q.description || t.dashboard.quizForLesson}
                    </p>
                    <div className="text-muted small mb-3">
                      <Clock size={14} className="me-1" />
                      {q.duration || 20} {t.quizzes.minutes}
                    </div>
                    <div className="mt-auto">
                      <Link
                        to={`/quiz/${encodeURIComponent(
                          q.moduleId
                        )}/${encodeURIComponent(q.lessonId)}`}
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-100"
                        >
                          {t.quizzes.startQuiz}
                        </Button>
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>
      

      {/* Letzte Quiz-Ergebnisse */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">{t.dashboard.recentQuizResults}</h2>
          <Link to="/Quizzes">
            <Button variant="outline-primary" size="sm">
              {t.dashboard.allQuizzes} <ArrowRight size={16} className="ms-1" />
            </Button>
          </Link>
        </div>

        <Row className="g-3">
          {quizResults.slice(0, 3).map((quiz) => {
            const lessonInfo = allLessons.find(
              (item) => item.lessonId === quiz.quiz_id
            );
            const quizTitle = lessonInfo
              ? lessonInfo.lesson.title
              : `Quiz #${quiz.quiz_id}`;
            const moduleTitle = lessonInfo ? lessonInfo.moduleTitle : "";

            return (
              <Col md={4} key={quiz.id}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1 pe-2">
                        <h5 className="mb-1">{quizTitle}</h5>
                        {moduleTitle && (
                          <small className="text-muted d-block mb-2">
                            {moduleTitle}
                          </small>
                        )}
                      </div>
                      <Badge
                        bg={
                          quiz.percentage >= 80
                            ? "success"
                            : quiz.percentage >= 60
                            ? "warning"
                            : "danger"
                        }
                        style={{ flexShrink: 0 }}
                      >
                        {quiz.percentage}%
                      </Badge>
                    </div>
                    <div className="d-flex align-items-center text-muted small mb-2">
                      <Trophy size={14} className="me-1" />
                      {quiz.score} {t.modules.points}
                    </div>
                    <div className="d-flex align-items-center text-muted small mb-2">
                      <Clock size={14} className="me-1" />
                      {new Date(quiz.created_date).toLocaleDateString("de-DE")}
                    </div>
                    <div className="d-flex align-items-center text-muted small mb-3">
                      <CheckCircle size={14} className="me-1" />
                      {quiz.correct_answers}/{quiz.total_questions} {t.dashboard.correct}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {quizResults.length === 0 && (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <Trophy size={48} className="text-muted mb-3" />
              <h5 className="mb-2">{t.dashboard.noQuizCompleted}</h5>
              <p className="text-muted mb-3">
                {t.dashboard.testKnowledge}
              </p>
              <Link to="/Quizzes">
                <Button variant="primary">{t.quizzes.startQuiz}</Button>
              </Link>
            </Card.Body>
          </Card>
        )}
      </div>
      
      </Container>
      
      </main>
      </>
  );
  
}
